import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { organizationAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Building2, Loader2, ArrowRight, CheckCircle } from 'lucide-react';

const Onboarding = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [orgData, setOrgData] = useState({
    name: '',
    preset: 'ph_private',
    timezone: 'Asia/Manila',
  });
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await organizationAPI.create(orgData);
      // Update user with organization_id
      updateUser({ ...user, organization_id: response.data.id, role: 'corporate_secretary' });
      setStep(2);
      
      // Redirect to dashboard after short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      console.error('Failed to create organization:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC] p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_860f89f1-5bcd-47ca-9a42-2845ecac666a/artifacts/4om46ky3_LexAgenda-final_logo.png"
            alt="LexAgenda"
            className="h-16 mx-auto mb-4"
          />
        </div>

        {step === 1 ? (
          <Card className="border-[#E2E6EA] shadow-lg">
            <CardHeader className="space-y-1">
              <CardTitle className="text-xl font-bold text-center flex items-center justify-center gap-2">
                <Building2 className="h-5 w-5 text-[#00A3A3]" />
                Setup Your Organization
              </CardTitle>
              <CardDescription className="text-center">
                Tell us about your company to get started
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Company Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="ABC Corporation"
                    value={orgData.name}
                    onChange={(e) => setOrgData({ ...orgData, name: e.target.value })}
                    required
                    data-testid="org-name-input"
                    className="border-[#E2E6EA] focus:border-[#00A3A3] focus:ring-[#00A3A3]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="preset">Company Type</Label>
                  <Select
                    value={orgData.preset}
                    onValueChange={(value) => setOrgData({ ...orgData, preset: value })}
                  >
                    <SelectTrigger data-testid="org-type-select" className="border-[#E2E6EA]">
                      <SelectValue placeholder="Select company type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ph_private">Philippine Private Corporation</SelectItem>
                      <SelectItem value="opc">One Person Corporation (OPC)</SelectItem>
                      <SelectItem value="generic">Generic / Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select
                    value={orgData.timezone}
                    onValueChange={(value) => setOrgData({ ...orgData, timezone: value })}
                  >
                    <SelectTrigger data-testid="org-timezone-select" className="border-[#E2E6EA]">
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Asia/Manila">Asia/Manila (PHT)</SelectItem>
                      <SelectItem value="Asia/Singapore">Asia/Singapore (SGT)</SelectItem>
                      <SelectItem value="Asia/Hong_Kong">Asia/Hong Kong (HKT)</SelectItem>
                      <SelectItem value="UTC">UTC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-[#F0F2F4] rounded-lg p-4 text-sm text-[#5A6570]">
                  <p className="font-medium text-[#1A1D21] mb-2">What's included:</p>
                  <ul className="space-y-1">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-[#00A3A3]" />
                      Pre-configured Board of Directors
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-[#00A3A3]" />
                      Stockholders meeting setup
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-[#00A3A3]" />
                      Philippine-compliant templates
                    </li>
                  </ul>
                </div>
              </CardContent>
              
              <CardFooter>
                <Button
                  type="submit"
                  className="w-full bg-[#00A3A3] hover:bg-[#008585] text-white"
                  disabled={loading || !orgData.name}
                  data-testid="org-create-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </CardFooter>
            </form>
          </Card>
        ) : (
          <Card className="border-[#E2E6EA] shadow-lg">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-[#00A3A3] rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-xl font-bold text-[#1A1D21] mb-2">You're all set!</h2>
              <p className="text-[#5A6570]">
                Your organization has been created. Redirecting to dashboard...
              </p>
              <div className="mt-4">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-[#00A3A3]" />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
